import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import DOMPurify from 'dompurify';
import { BsModalRef } from 'ngx-bootstrap/modal';

@Component({
	selector: 'app-show-form-history',
	standalone: true,
	imports: [CommonModule],
	templateUrl: './show-form-history.component.html',
	styleUrls: ['./show-form-history.component.css'],
	encapsulation: ViewEncapsulation.None,
})
export class ShowFormHistoryComponent implements OnInit, OnDestroy {
	private _summary = '';
	private _isLoading = false;

	@Input()
	get summary(): string {
		return this._summary;
	}
	set summary(value: string) {
		const normalized = value ?? '';
		if (normalized === this._summary) return;
		this._summary = normalized;
		this.updateSanitizedSummary();
	}

	@Input()
	get isLoading(): boolean {
		return this._isLoading;
	}
	set isLoading(value: boolean) {
		const next = !!value;
		if (next === this._isLoading) return;
		this._isLoading = next;
		if (this._isLoading) this.startLoadingMessageRotation();
		else this.stopLoadingMessageRotation();
	}

	loadingMessage = 'Reviewing activity for this form…';
	sanitizedSummary = '';

	private readonly loadingMessages: string[] = [
		'Reviewing activity for this form…',
		'Scanning change history…',
		'Cross-checking related records…',
		'Filtering workflow/system noise…',
		'Summarizing what matters to business users…',
		'Finalizing the recap…',
	];

	private loadingTimerId: number | null = null;
	private loadingIndex = 0;

	constructor(public readonly bsModalRef: BsModalRef) {}

	ngOnInit(): void {
		if (this.isLoading) this.startLoadingMessageRotation();
		this.updateSanitizedSummary();
	}

	ngOnDestroy(): void {
		this.stopLoadingMessageRotation();
	}

	close(): void {
		this.stopLoadingMessageRotation();
		this.bsModalRef.hide();
	}

	private updateSanitizedSummary(): void {
		const raw = this.summary ?? '';

		this.sanitizedSummary =
			DOMPurify.sanitize(raw, {
				ALLOWED_TAGS: ['div', 'span', 'b', 'strong', 'br'],
				ALLOWED_ATTR: ['class'],
				FORBID_TAGS: ['style', 'script', 'iframe', 'object', 'embed', 'svg', 'math', 'link', 'meta'],
				ALLOW_DATA_ATTR: false,
			}) || '';
	}

	private startLoadingMessageRotation(): void {
		// Avoid double timers
		if (this.loadingTimerId != null) return;

		// reset
		this.loadingIndex = 0;
		this.loadingMessage = this.loadingMessages[this.loadingIndex];

		this.loadingTimerId = window.setInterval(() => {
			this.loadingIndex = (this.loadingIndex + 1) % this.loadingMessages.length;
			this.loadingMessage = this.loadingMessages[this.loadingIndex];
		}, 4000);
	}

	private stopLoadingMessageRotation(): void {
		if (this.loadingTimerId != null) {
			window.clearInterval(this.loadingTimerId);
			this.loadingTimerId = null;
		}
	}
}
